import React, { useState, useEffect } from 'react';
import AttributedOrders from '../components/AttributedOrders';

const PROXY = 'https://crabplace-google-ads.netlify.app/.netlify/functions';

const DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
];

const TABS = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'pmax', label: 'Performance Max' },
  { value: 'search', label: 'Search' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'display', label: 'Display' },
];

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtN(n) {
  if (n == null || n === '—') return '—';
  return parseFloat(n).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function StatusBadge({ status }) {
  const s = (status || '').replace('_', ' ');
  const active = s === 'ENABLED';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: active ? '#e6f9f0' : '#f5f5f5',
      color: active ? '#1a8a4a' : 'var(--gray-500)',
      textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {s}
    </span>
  );
}

function TypeBadge({ type }) {
  const labels = {
    PERFORMANCE_MAX: 'PMax',
    SEARCH: 'Search',
    SHOPPING: 'Shopping',
    DISPLAY: 'Display',
    VIDEO: 'Video',
    DISCOVERY: 'Discovery',
    DEMAND_GEN: 'Demand Gen',
  };
  const colors = {
    PERFORMANCE_MAX: { bg: '#e8f0fe', color: '#1a73e8' },
    SEARCH: { bg: '#e6f9f0', color: '#1a8a4a' },
    SHOPPING: { bg: '#fff8e6', color: '#b06a00' },
    DISPLAY: { bg: '#f3e8ff', color: '#7c3aed' },
  };
  const c = colors[type] || { bg: '#f5f5f5', color: '#5f6368' };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: c.bg, color: c.color, textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {labels[type] || type || '—'}
    </span>
  );
}

export default function GoogleCampaigns() {
  const [days, setDays] = useState('30');
  const [tab, setTab] = useState('all');
  const [data, setData] = useState(null);
  const [pmaxData, setPmaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPmax, setExpandedPmax] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    setPmaxData(null);

    Promise.all([
      fetch(`${PROXY}/campaign-performance?days=${days}`).then(r => r.json()),
      fetch(`${PROXY}/pmax-performance?days=${days}`).then(r => r.json()),
    ])
      .then(([campRes, pmaxRes]) => {
        if (campRes.error) throw new Error(typeof campRes.error === 'string' ? campRes.error : JSON.stringify(campRes.error));
        setData(campRes);
        setPmaxData(pmaxRes);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  const campaigns = data?.campaigns || [];
  const totals = data?.totals || {};

  // Date window matching the `days` selector, so our last-click numbers line up
  // with Google's reported conversions for the comparison strip.
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const winTo = new Date();
  const winFrom = new Date(winTo); winFrom.setDate(winFrom.getDate() - (parseInt(days, 10) - 1));
  const winLabel = (DATE_RANGES.find(r => r.value === days) || {}).label;

  // Filter by tab
  const typeMap = { pmax: 'PERFORMANCE_MAX', search: 'SEARCH', shopping: 'SHOPPING', display: 'DISPLAY' };
  const filtered = tab === 'all' ? campaigns : campaigns.filter(c => c.type === typeMap[tab]);

  // Asset groups for Pmax drill-down
  const assetGroups = pmaxData?.asset_groups || [];

  const thStyle = { textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', fontSize: 11 };
  const tdStyle = { padding: '7px 8px', fontSize: 12 };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Google Campaigns</h2>
        <select
          value={days}
          onChange={e => setDays(e.target.value)}
          style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, color: 'var(--navy)', fontFamily: 'Montserrat', fontWeight: 600, cursor: 'pointer' }}
        >
          {DATE_RANGES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      {/* Summary KPIs */}
      {!loading && !error && data && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">Total Spend</div>
            <div className="kpi-value">{fmt$(totals.spend)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Clicks</div>
            <div className="kpi-value">{(totals.clicks || 0).toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Impressions</div>
            <div className="kpi-value">{(totals.impressions || 0).toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Conversions</div>
            <div className="kpi-value">{fmtN(totals.conversions)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Revenue</div>
            <div className="kpi-value">{fmt$(totals.conversion_value)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">ROAS</div>
            <div className="kpi-value" style={{ color: parseFloat(totals.roas) >= 1 ? 'var(--green, #1a8a4a)' : 'var(--red, #c0392b)' }}>
              {fmtN(totals.roas)}x
            </div>
          </div>
        </div>
      )}

      {/* Tab filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              fontSize: 11, fontWeight: tab === t.value ? 700 : 500, padding: '4px 12px', borderRadius: 14,
              border: tab === t.value ? '2px solid var(--orange)' : '1px solid var(--gray-200)',
              background: tab === t.value ? 'var(--orange)' : '#fff',
              color: tab === t.value ? '#fff' : 'var(--navy)',
              cursor: 'pointer', fontFamily: 'Montserrat',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Campaign Table */}
      <div className="card">
        <div className="card-header">Campaigns</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red, #c0392b)' }}>Error: {error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No campaigns found.</div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ ...thStyle, textAlign: 'left', padding: '8px 12px' }}>Campaign</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Type</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Spend</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Impressions</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Clicks</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>CTR</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>CPC</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Conv</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Revenue</th>
                  <th style={{ ...thStyle, textAlign: 'right', padding: '8px 12px' }}>ROAS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const isPmax = c.type === 'PERFORMANCE_MAX';
                  const isExpanded = expandedPmax === c.id;
                  const campAssetGroups = isPmax ? assetGroups.filter(ag => ag.campaign === c.name) : [];
                  const roas = parseFloat(c.roas || 0);

                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        style={{
                          borderBottom: '1px solid var(--gray-100)',
                          background: i % 2 === 0 ? '#fff' : 'var(--gray-50)',
                          cursor: isPmax && campAssetGroups.length > 0 ? 'pointer' : 'default',
                        }}
                        onClick={() => isPmax && campAssetGroups.length > 0 && setExpandedPmax(isExpanded ? null : c.id)}
                      >
                        <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600, maxWidth: 260 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {isPmax && campAssetGroups.length > 0 && (
                              <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>{isExpanded ? '▼' : '▶'}</span>
                            )}
                            {c.name}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}><TypeBadge type={c.type} /></td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={c.status} /></td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--navy)', fontWeight: 600 }}>{fmt$(c.cost)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parseInt(c.impressions || 0).toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parseInt(c.clicks || 0).toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{c.ctr || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt$(c.avg_cpc)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(c.conversions) > 0 ? fmtN(c.conversions) : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: parseFloat(c.conversion_value) > 0 ? 600 : 400 }}>
                          {parseFloat(c.conversion_value) > 0 ? fmt$(c.conversion_value) : '—'}
                        </td>
                        <td style={{
                          padding: '8px 12px', textAlign: 'right', fontWeight: roas > 0 ? 700 : 400,
                          color: roas >= 1 ? 'var(--green, #1a8a4a)' : roas > 0 ? 'var(--red, #c0392b)' : 'inherit',
                        }}>
                          {roas > 0 ? fmtN(roas) + 'x' : '—'}
                        </td>
                      </tr>
                      {/* Pmax asset groups drill-down */}
                      {isExpanded && campAssetGroups.map((ag, j) => (
                        <tr key={`ag-${j}`} style={{ background: '#f8f9ff', borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '6px 12px 6px 36px', fontSize: 11, color: 'var(--gray-600)' }}>
                            {ag.asset_group}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}>ASSET GROUP</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={ag.status} /></td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>{fmt$(ag.cost)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>{parseInt(ag.impressions || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>{parseInt(ag.clicks || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>—</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>—</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>{parseFloat(ag.conversions) > 0 ? fmtN(ag.conversions) : '—'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)' }}>{parseFloat(ag.conversion_value) > 0 ? fmt$(ag.conversion_value) : '—'}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--gray-600)' }}>—</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AttributedOrders
        channel="google"
        from={ymd(winFrom)}
        to={ymd(winTo)}
        windowLabel={winLabel}
        comparison={{
          label: 'Google',
          count: totals.conversions,
          value: totals.conversion_value,
          color: '#1a73e8',
          providerNote: 'Google Ads — may include non-purchase conversions',
        }}
      />
    </div>
  );
}
